import { useQuery } from '@tanstack/react-query';
import type { AIPlan } from '@notion-training/shared';
import { LEVELS } from '@notion-training/shared';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api.js';
import { getNextActionLevel } from './getNextActionLevel.js';

type Props = {
  plan: AIPlan;
  remainingToday: number;
  onUpdatePlan: () => void;
};

type LessonSummary = { slug: string; title: string; level: number; order: number };
type LessonsResponse = { lessons: LessonSummary[] };

const COUNTS_BY_TAG = (plan: AIPlan) => {
  const result = { priority: 0, useful: 0, skip: 0 };
  for (const lv of plan.levels) result[lv.tag] += 1;
  return result;
};

const LESSON_COUNTS_BY_TAG = (plan: AIPlan) => {
  const result = { priority: 0, useful: 0, skip: 0 };
  for (const lv of plan.levels) {
    const meta = LEVELS.find(l => l.id === lv.level);
    if (!meta) continue;
    result[lv.tag] += meta.expectedLessons;
  }
  return result;
};

const formatGeneratedAt = (iso: string) => {
  const d = new Date(iso);
  const minutesAgo = Math.floor((Date.now() - d.getTime()) / 60000);
  if (minutesAgo < 1) return 'just now';
  if (minutesAgo < 60) return `${minutesAgo} minute${minutesAgo === 1 ? '' : 's'} ago`;
  const hoursAgo = Math.floor(minutesAgo / 60);
  if (hoursAgo < 24) return `${hoursAgo} hour${hoursAgo === 1 ? '' : 's'} ago`;
  const daysAgo = Math.floor(hoursAgo / 24);
  return `${daysAgo} day${daysAgo === 1 ? '' : 's'} ago`;
};

export function PlanCard({ plan, remainingToday, onUpdatePlan }: Props) {
  const tagCounts = COUNTS_BY_TAG(plan);
  const lessonCounts = LESSON_COUNTS_BY_TAG(plan);
  const nextLevel = getNextActionLevel(plan);
  const nextMeta = nextLevel ? LEVELS.find(l => l.id === nextLevel.level) : null;

  // Look up published lessons so we can deep-link to the first one in nextLevel.
  const lessonsQuery = useQuery<LessonsResponse>({
    queryKey: ['lessons'],
    queryFn: () => api.get<LessonsResponse>('/api/lessons'),
  });

  const firstLessonOfNextLevel = nextLevel
    ? (lessonsQuery.data?.lessons ?? [])
        .filter((l) => l.level === nextLevel.level)
        .sort((a, b) => a.order - b.order)[0]
    : undefined;

  return (
    <div
      style={{
        background: 'linear-gradient(135deg, rgba(13,40,24,1) 0%, rgba(10,31,18,1) 100%)',
        border: '1px solid rgba(79,233,181,0.32)',
        borderRadius: 12,
        padding: '28px 32px',
        marginBottom: 18,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div className="eyebrow" style={{ color: 'var(--color-accent)' }}>▌ Your AI plan</div>
          <h2 style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.02em', margin: '6px 0 0', color: 'var(--color-text)' }}>
            {tagCounts.priority} priority · {tagCounts.useful} useful · {tagCounts.skip} skip
          </h2>
        </div>
        <div style={{ display: 'flex', gap: 18, fontSize: 11, color: 'var(--color-text-3)' }}>
          <Counter num={lessonCounts.priority} label="recommended" tone="accent" />
          <Counter num={lessonCounts.useful} label="optional" tone="muted" />
          <Counter num={lessonCounts.skip} label="skip" tone="dim" />
        </div>
      </div>

      <p style={{ color: 'var(--color-text-2)', fontSize: 14, lineHeight: 1.6, margin: '0 0 20px', maxWidth: 640 }}>
        {plan.intro}
      </p>

      {nextLevel && nextMeta && (
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          {firstLessonOfNextLevel ? (
            <Link to={`/lesson/${firstLessonOfNextLevel.slug}`} className="btn btn-primary">
              ▸ Start: Level {nextMeta.id} — {nextMeta.shortTitle}
            </Link>
          ) : (
            <button
              type="button"
              disabled
              className="btn"
              style={{ opacity: 0.55, cursor: 'not-allowed' }}
              title={`Level ${nextMeta.id} hasn't been authored yet — coming soon.`}
            >
              ⏳ Coming soon: Level {nextMeta.id} — {nextMeta.shortTitle}
            </button>
          )}
          <span style={{ color: 'var(--color-text-3)', fontSize: 12 }}>
            {firstLessonOfNextLevel
              ? `${nextLevel.tag === 'priority' ? 'Top priority' : 'Recommended'} · ${nextMeta.expectedLessons} lessons`
              : `Level still being authored · ${nextMeta.expectedLessons} planned lessons`}
          </span>
        </div>
      )}

      <div style={{ borderTop: '1px solid rgba(79,233,181,0.16)', marginTop: 22, paddingTop: 14, display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--color-text-3)', flexWrap: 'wrap', gap: 8 }}>
        <span>Generated {formatGeneratedAt(plan.generatedAt)}</span>
        <span>
          <button
            onClick={onUpdatePlan}
            disabled={remainingToday === 0}
            style={{ background: 'transparent', color: 'var(--color-text-3)', border: 0, textDecoration: 'underline', cursor: remainingToday === 0 ? 'not-allowed' : 'pointer', fontSize: 11, padding: 0 }}
          >
            Update plan
          </button>{' '}
          · {remainingToday} of 3 generations left today
        </span>
      </div>
    </div>
  );
}

function Counter({ num, label, tone }: { num: number; label: string; tone: 'accent' | 'muted' | 'dim' }) {
  const colors = {
    accent: 'var(--color-accent)',
    muted: 'var(--color-text-2)',
    dim: 'var(--color-text-4)',
  };
  return (
    <div>
      <div style={{ color: colors[tone], fontSize: 18, fontWeight: 700 }}>{num}</div>
      <div>{label}</div>
    </div>
  );
}
